import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Button, Input, Field, FieldLabel, FieldError } from '../components/ui';
import { SocialButton } from '../components/ui/SocialButton';
import { PlatformCheckoutBrand } from '../components/auth/PlatformCheckoutBrand';
import { PlatformCheckoutShell } from '../components/auth/PlatformCheckoutShell';
import { useAuthStore } from '../store/auth';
import { hasRole, hasAnyRole } from '@court-sync/shared';
import {
  sanitizeLoginBannerMessage,
  shouldDeferPlatformEmailVerification,
} from '../utils/emailVerificationRedirect';
import { fetchSubscriptionQuery } from '../queries/subscription';
import { buildSubscriptionStatusFromResponse } from '../utils/subscriptionStatus';
import {
  hasAcademyMemberContext,
  needsPlatformOrgOnboarding,
} from '../utils/platformLoginRedirect';
import {
  consumeSocialAuthRedirectIfReady,
  isSocialAuthRedirectInitiatedError,
  prepareSocialAuthContext,
  readSocialAuthRedirectError,
} from '../utils/socialAuthRedirect';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle, loginWithFacebook, loginWithGithub, user, isInitialized } =
    useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);

  // Get message and email from location state or query params (e.g. post-verification redirect)
  const message =
    location.state?.message ?? sanitizeLoginBannerMessage(searchParams.get('message')) ?? undefined;
  const prefillEmail =
    location.state?.email ?? searchParams.get('email') ?? undefined;
  const pendingBookingId = location.state?.pendingBookingId || localStorage.getItem('pendingBookingId');

  // Get returnUrl from query params
  const returnUrl = searchParams.get('returnUrl');

  // Helper function to redirect users based on their role and academy association
  const handleUserRedirect = async (user: any, options?: { isSocialLogin?: boolean }) => {
    const isSocialLogin = options?.isSocialLogin ?? false;

    // Check for returnUrl first (highest priority)
    if (returnUrl) {
      // Clear pendingBookingId from localStorage if it exists
      if (pendingBookingId) {
        localStorage.removeItem('pendingBookingId');
      }
      navigate(returnUrl);
      return true;
    }

    // Check for pendingBookingId - redirect to booking page
    if (pendingBookingId) {
      localStorage.removeItem('pendingBookingId');
      // Extract academy slug and lesson ID from returnUrl if available, or use booking data
      // For now, we'll construct a URL that includes the pendingBookingId
      // The PublicBookingPage will handle claiming the booking
      const bookingUrl = returnUrl || `/academy/${location.pathname.split('/')[2]}/book/${location.pathname.split('/')[4] || 'pending'}?pendingBookingId=${pendingBookingId}`;
      navigate(bookingUrl);
      return true;
    }

    // Platform admin goes to backoffice
    if (hasRole(user, 'admin')) {
      navigate('/backoffice/risk');
      return true;
    }

    // Org operators who registered but have not finished academy/club setup
    if (needsPlatformOrgOnboarding(user)) {
      navigate('/academy-setup', {
        state: {
          email: user.email,
          message: 'Welcome back! Pick up where you left off setting up your organization.',
          isSocialLogin: options?.isSocialLogin,
        },
      });
      return true;
    }

    // Platform login prioritizes academy management when the user actually owns/admins
    // an academy. A guardian/student role at another academy must NOT divert them to the
    // academy picker here — that flow is reserved for the academy-scoped login form.
    if (
      (hasRole(user, 'academy_owner') || hasRole(user, 'academy_admin')) &&
      !!user.academyId
    ) {
      navigate('/admin/dashboard');
      return true;
    }

    // Guardians with an academy profile use academy picker (auto-redirects when single profile)
    if (hasAcademyMemberContext(user)) {
      navigate('/academy-picker');
      return true;
    }

    // Users who booked as guardian but got academy_owner from social-login default:
    // prefer guardian routing when they have guardian/student for an academy but don't own one
    const hasGuardianForAcademy =
      user?.rolesWithAcademy?.some(
        (r: { role: string; academyId: string | null }) =>
          (r.role === 'guardian' || r.role === 'student') && r.academyId != null
      ) ?? false;
    const hasStaleAcademyOwner =
      user?.rolesWithAcademy?.some(
        (r: { role: string; academyId: string | null }) =>
          r.role === 'academy_owner' && r.academyId == null
      ) ?? false;
    const doesNotOwnAcademy = !user?.academyId;

    if (
      hasGuardianForAcademy &&
      hasStaleAcademyOwner &&
      doesNotOwnAcademy &&
      hasAnyRole(user, ['guardian', 'student'])
    ) {
      navigate('/academy-picker');
      return true;
    }

    // Club operators with an organization go to the unified management dashboard
    if (hasRole(user, 'club_owner') || hasRole(user, 'club_admin')) {
      navigate('/admin/dashboard');
      return true;
    }

    // For academy owners, check if they need academy setup
    if (hasRole(user, 'academy_owner')) {
      const hasAcademy = !!user.academyId;

      // If no academy, go to academy setup
      if (!hasAcademy) {
        navigate('/academy-setup', {
          state: {
            email: user.email,
            message: 'Please set up your tennis academy.',
            isSocialLogin: true,
          }
        });
        return true;
      }

      // Has academy - go to academy owner dashboard
      navigate('/admin/dashboard');
      return true;
    }

    // Social login from platform: users without an org entity go to setup
    // Exclude guardian, student, coach, club_owner — they have their own flows
    if (
      isSocialLogin &&
      !user?.academyId &&
      !hasRole(user, 'academy_owner') &&
      !hasRole(user, 'club_owner') &&
      !hasRole(user, 'guardian') &&
      !hasRole(user, 'student') &&
      !hasRole(user, 'coach')
    ) {
      navigate('/academy-setup', {
        state: {
          email: user.email,
          message: "Welcome! Let's set up your organization on CourtSync.",
          isSocialLogin: true,
        }
      });
      return true;
    }

    // Default redirect – DashboardRouter will route by role
    navigate('/dashboard');
    return true;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: prefillEmail ?? '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      await login(data.email, data.password);

      // Get the logged-in user from auth store
      const { user: loggedInUser } = useAuthStore.getState();
      if (loggedInUser) {
        if (!loggedInUser.isEmailVerified) {
          let hasActiveSubscription = false;
          if (hasRole(loggedInUser, 'academy_owner') || hasRole(loggedInUser, 'club_owner')) {
            try {
              const response = await fetchSubscriptionQuery();
              hasActiveSubscription =
                buildSubscriptionStatusFromResponse(response).hasActiveSubscription;
            } catch {
              hasActiveSubscription = false;
            }
          }

          const deferVerification = shouldDeferPlatformEmailVerification(loggedInUser, {
            returnUrl,
            hasActiveSubscription,
          });

          if (!deferVerification) {
            const params = new URLSearchParams({
              email: loggedInUser.email,
            });

            if (returnUrl) {
              params.set('returnUrl', returnUrl);
            } else {
              params.set(
                'returnUrl',
                loggedInUser.roles?.includes('admin') ? '/backoffice/risk' : '/dashboard'
              );
            }

            navigate(`/email-verification?${params.toString()}`);
            return;
          }
        }

        await handleUserRedirect(loggedInUser);
      } else {
        // Fallback if user not in store yet (race condition) - /dashboard will route by role
        navigate('/dashboard');
      }
    } catch (error: any) {
      setError('root', {
        message: error.message || 'Invalid email or password',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    const redirectError = readSocialAuthRedirectError();
    if (redirectError) {
      setError('root', { message: redirectError });
    }
  }, [isInitialized, setError]);

  useEffect(() => {
    if (!isInitialized || !user) return;

    const consumed = consumeSocialAuthRedirectIfReady();
    const isSocialReturn =
      consumed?.hadRedirect === true &&
      (consumed.pending == null || consumed.pending.flow === 'login');

    void handleUserRedirect(user, { isSocialLogin: isSocialReturn });
  }, [isInitialized, user]);

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'github') => {
    try {
      setSocialLoading(provider);
      prepareSocialAuthContext({
        flow: 'login',
        provider,
        returnUrl,
        pendingBookingId,
      });

      let signedInUser;
      switch (provider) {
        case 'google':
          signedInUser = await loginWithGoogle();
          break;
        case 'facebook':
          signedInUser = await loginWithFacebook();
          break;
        case 'github':
          signedInUser = await loginWithGithub();
          break;
      }

      if (signedInUser) {
        // Use the redirect helper to handle all redirect logic (isSocialLogin for platform redirect rules)
        await handleUserRedirect(signedInUser, { isSocialLogin: true });
      } else {
        // Fallback - check for returnUrl
        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error: unknown) {
      if (isSocialAuthRedirectInitiatedError(error)) {
        return;
      }
      const message =
        (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : null) || `Failed to sign in with ${provider}`;
      setError('root', { message });
    } finally {
      setSocialLoading(null);
    }
  };

  const loginLeft = (
    <>
      <PlatformCheckoutBrand className="mb-4 md:mb-8" />
      <h1 className="mb-2 text-xl font-bold text-gray-900 dark:text-white sm:mb-3 sm:text-2xl md:text-3xl">
        Welcome back
      </h1>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 sm:mb-6 sm:text-base">
        Sign in to manage your academy, lessons, bookings, and payments in one place.
      </p>
      <ul className="hidden space-y-3 text-sm text-gray-600 dark:text-gray-400 sm:block">
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>Secure access to your dashboard</span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>Google sign-in or email</span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>Reset your password anytime from the form</span>
        </li>
      </ul>
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-500 sm:mt-8">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
          Create an account
        </Link>
      </p>
    </>
  );

  return (
    <PlatformCheckoutShell asideVariant="onboarding" left={loginLeft}>
      <div className="mx-auto w-full max-w-md lg:mx-0 lg:ml-10 lg:max-w-lg">
        <div>
          <div className="mb-6 space-y-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Sign in
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Continue with Google or your email
            </p>
          </div>

          {message && (
            <div
              className={`mb-6 p-4 rounded-lg border ${
                message.includes('verified successfully')
                  ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
                  : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
              }`}
            >
              <div className="flex items-center">
                {message.includes('verified successfully') ? (
                  <CheckCircle className="h-5 w-5 mr-2 shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2 shrink-0" />
                )}
                <p className="text-sm font-medium">{message}</p>
              </div>
              {prefillEmail && (
                <p className="text-xs mt-1 opacity-75">
                  Email: {prefillEmail}
                </p>
              )}
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-3">
              <SocialButton
                provider="google"
                onClick={() => handleSocialLogin('google')}
                isLoading={socialLoading === 'google'}
                disabled={!!socialLoading || isLoading}
              >
                Continue with Google
              </SocialButton>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                  Or continue with email
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
            <div className="space-y-4">
              <Field data-invalid={!!errors.email?.message}>
                <FieldLabel htmlFor="email">Email address</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email?.message}
                  {...register('email')}
                />
                <FieldError>{errors.email?.message}</FieldError>
              </Field>

              <Field data-invalid={!!errors.password?.message}>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password?.message}
                  {...register('password')}
                />
                <FieldError>{errors.password?.message}</FieldError>
              </Field>

              {errors.root && (
                <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="text-sm text-red-800 dark:text-red-400">
                    {errors.root.message}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col space-y-4 pt-2">
              <Button
                type="submit"
                className="w-full btn-primary"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors duration-200"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>
          </form>

          <p className="mt-8 text-xs text-gray-500 dark:text-gray-400">
            By signing in, you agree to our{' '}
            <Link
              to="/terms"
              className="underline hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="underline hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </PlatformCheckoutShell>
  );
}
